import { Download, FolderPlus, Link2, Mail, Star, Tag, X } from 'lucide-react';
import type { ReactNode } from 'react';
import type { CandidateBrowseRow, CandidateBulkRequest, CandidateBulkResult } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { downloadCsv, exportFileName, exportValueOf, toCsv } from '../../../../components/data-table/table-export';
import type { Column } from '../../../../components/data-table/types';
import { ModuleGate } from '../../../../components/ModuleGate';
import { Popover, usePopover } from '../../../../components/Popover';
import { errorMessage } from '../../../../lib/api-client';
import { formatNumber } from '../../../../lib/format';
import { EMPTY_SELECTION, isSelected, selectAllMatching, selectionCount, type SelectionState } from '../../../../lib/selection';
import { useToasts } from '../../../../stores/toasts';
import { AddToCvBankButton } from '../../../cv-bank/components/AddToCvBankButton';
import { ComposePanel } from '../../../communication/components/ComposePanel';
import { AddToPoolPanel } from '../../../pools/components/PoolPanels';
import { useCandidateBulk } from '../../api/candidate-browse-api';
import { TagPicker, type TagChoice } from './TagPicker';
import styles from './BulkBar.module.css';

interface BulkBarProps {
  readonly selection: SelectionState;
  readonly onSelectionChange: (selection: SelectionState) => void;
  readonly total: number;
  /** The rows on screen: what the actions that run here (CV Bank, links, export, pools) act on. */
  readonly rows: readonly CandidateBrowseRow[];
  /** The address's filters, sent for "all matching". */
  readonly filters: Readonly<Record<string, string>>;
  readonly columns: readonly Column<CandidateBrowseRow>[];
  readonly canEdit: boolean;
}

/** The bar over the list once someone is ticked (mockup Main.dc.html; 06-qol §3). */
export function BulkBar({ selection, onSelectionChange, total, rows, filters, columns, canEdit }: BulkBarProps): ReactNode {
  const bulk = useCandidateBulk();
  const show = useToasts((state) => state.show);
  const { anchor: emailAnchor, triggerRef: emailTrigger, isOpen: isEmailOpen, toggle: toggleEmail, close: closeEmail } = usePopover();
  const { anchor: tagAnchor, triggerRef: tagTrigger, toggle: toggleTag, close: closeTag } = usePopover();
  const { anchor: poolAnchor, triggerRef: poolTrigger, isOpen: isPoolOpen, toggle: togglePool, close: closePool } = usePopover();
  const count = selectionCount(selection, total) ?? 0;
  const picked = rows.filter((row) => isSelected(selection, row.candidate.id));
  const isAllMatching = selection.mode === 'all-matching';
  const people = `${formatNumber(count)} ${count === 1 ? 'person' : 'people'}`;
  // Server-side actions take the filters when everyone matching is selected; the rest act on the loaded rows.
  const target: Pick<CandidateBulkRequest, 'candidateIds' | 'matching'> = isAllMatching ? { matching: filters } : { candidateIds: [...selection.ids] };
  const loadedNote = isAllMatching && picked.length < count ? `Only the ${formatNumber(picked.length)} on this page — the rest aren't loaded.` : undefined;

  const run = async (input: CandidateBulkRequest, message: (result: CandidateBulkResult) => string): Promise<void> => {
    try {
      const result = await bulk.mutateAsync(input);
      show({ tone: 'success', message: message(result), detail: result.unchanged > 0 ? `${formatNumber(result.unchanged)} already had it.` : undefined });
      onSelectionChange(EMPTY_SELECTION);
    } catch (error) {
      show({ tone: 'danger', message: "That didn't go through", detail: errorMessage(error) });
    }
  };

  const addTag = (choice: TagChoice): void => {
    closeTag();
    void run({ ...target, action: 'add_tag', ...(choice.tagId ? { tagId: choice.tagId } : { tagName: choice.name }) }, (result) => `“${choice.name}” added to ${formatNumber(result.done)}`);
  };

  const copyLinks = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(picked.map((row) => new URL(`/candidates/${row.candidate.id}`, document.baseURI).href).join('\n'));
      show({ tone: 'success', message: `${formatNumber(picked.length)} ${picked.length === 1 ? 'link' : 'links'} copied`, detail: loadedNote });
    } catch {
      show({ tone: 'danger', message: "The links weren't copied", detail: 'This browser blocked the clipboard. Try again from the page itself.' });
    }
  };

  const exportSelected = (): void => {
    const filled = columns.filter((column) => picked.some((row) => exportValueOf(column, row) !== ''));
    downloadCsv(exportFileName('candidates'), toCsv(filled, picked));
    show({ tone: 'success', message: `${formatNumber(picked.length)} ${picked.length === 1 ? 'row' : 'rows'} exported`, detail: loadedNote });
  };

  return (
    <div className={styles.bar} role="region" aria-label="Selected candidates">
      <span className={styles.count}>{formatNumber(count)} selected</span>
      {!isAllMatching && total > count && (
        <button type="button" className={styles.link} onClick={() => onSelectionChange(selectAllMatching(selection))}>
          Select all {formatNumber(total)} matching
        </button>
      )}
      <span className={styles.divider} aria-hidden />
      {canEdit && (
        <>
          <Button ref={emailTrigger} variant="outline" size="sm" icon={Mail} aria-haspopup="dialog" onClick={toggleEmail}>
            Email
          </Button>
          <Popover anchor={emailAnchor} onClose={closeEmail} width={460} label="Email the selected people">
            {isEmailOpen && (
              <ComposePanel
                recipientsLabel={`${people} — each filled in with their own name and job`}
                onCancel={closeEmail}
                onDone={closeEmail}
                onSend={async (draft) => {
                  // F15.03: never undoable (§L9), so it is sent as written.
                  const result = await bulk.mutateAsync({ ...target, action: 'message', templateKey: draft.templateKey, subject: draft.subject, body: draft.body });
                  show({ tone: 'success', message: `${formatNumber(result.done)} ${result.done === 1 ? 'email' : 'emails'} on their way`, detail: 'Each is on that person’s timeline.' });
                  onSelectionChange(EMPTY_SELECTION);
                }}
              />
            )}
          </Popover>
          <Button ref={tagTrigger} variant="outline" size="sm" icon={Tag} aria-haspopup="dialog" onClick={toggleTag}>
            Add tag
          </Button>
          <Popover anchor={tagAnchor} onClose={closeTag} label="Add a tag to the selected people">
            <TagPicker onPick={addTag} />
          </Popover>
        </>
      )}
      <Button variant="outline" size="sm" icon={Star} isPending={bulk.isPending && bulk.variables?.action === 'star'} onClick={() => void run({ ...target, action: 'star' }, (result) => `${formatNumber(result.done)} starred`)}>
        Star
      </Button>
      <AddToCvBankButton people={picked.map((row) => ({ candidateId: row.candidate.id, name: row.candidate.fullName }))} onDone={() => onSelectionChange(EMPTY_SELECTION)} />
      {canEdit && (
        <ModuleGate module="pools">
          <Button ref={poolTrigger} variant="outline" size="sm" icon={FolderPlus} aria-haspopup="dialog" onClick={togglePool}>
            Add to pool
          </Button>
          <Popover anchor={poolAnchor} onClose={closePool} width={380} label="Add to pool">
            {isPoolOpen && (
              <AddToPoolPanel
                candidateIds={picked.map((row) => row.candidate.id)}
                who={picked.length === 1 ? (picked[0]?.candidate.fullName ?? 'them') : `${formatNumber(picked.length)} people`}
                onCancel={closePool}
                onDone={() => {
                  closePool();
                  onSelectionChange(EMPTY_SELECTION);
                }}
              />
            )}
          </Popover>
        </ModuleGate>
      )}
      <Button variant="outline" size="sm" icon={Link2} onClick={() => void copyLinks()}>
        Copy links
      </Button>
      <Button variant="outline" size="sm" icon={Download} onClick={exportSelected}>
        Export selected
      </Button>
      <span className={styles.grow} />
      <Button variant="ghost" size="sm" icon={X} aria-label="Clear selection" onClick={() => onSelectionChange(EMPTY_SELECTION)} />
    </div>
  );
}
